/* eslint-disable react/jsx-no-bind */
import React from 'react';
import { InputBaseProps, SxProps, TextField } from '@mui/material';
import {
  Controller,
  FieldValues,
  RegisterOptions,
  ValidationRule,
  useFormContext,
} from 'react-hook-form';

interface Props {
  name: string;
  placeholder?: string;
  isError?: boolean;
  minValue?: number;
  maxValue?: number;
  maxLength?: ValidationRule<number>;
  minLength?: ValidationRule<number>;
  isRequired?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  label?: string;
  isMultiline?: boolean;
  rows?: number;
  sx?: SxProps;
  variant?: 'standard' | 'filled' | 'outlined';
  defaultValue?: string;
  disabled?: boolean;
  InputProps?: InputBaseProps['inputProps'];
  value?: string;
  onChangeFn?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  pattern?: ValidationRule<RegExp>;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
}

export function AppControlledTextField({
  name,
  placeholder,
  sx,
  label,
  isMultiline,
  rows,
  isError,
  minValue,
  maxValue,
  maxLength,
  minLength,
  isRequired = false,
  helperText = '',
  fullWidth = false,
  variant,
  defaultValue,
  disabled,
  InputProps,
  value,
  onChangeFn,
  pattern,
  rules,
}: Props): JSX.Element {
  const { control, register } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: {
          value: isRequired,
          message: 'This field is required',
        },
        min: minValue,
        max: maxValue,
        maxLength,
        minLength,
        pattern,
        ...rules,
      }}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => (
        <TextField
          {...register(name)}
          defaultValue={defaultValue}
          value={field.value ?? value ?? ''}
          disabled={disabled}
          variant={variant}
          fullWidth={fullWidth}
          id={name}
          placeholder={placeholder}
          label={isRequired ? `${label}*` : label}
          error={!!fieldState.error || !!isError}
          helperText={fieldState.error?.message || helperText}
          multiline={isMultiline}
          rows={rows}
          sx={sx}
          inputProps={InputProps}
          onChange={(e) => {
            field.onChange(e.target.value);
            if (onChangeFn) {
              onChangeFn(e as React.ChangeEvent<HTMLInputElement>);
            }
          }}
        />
      )}
    />
  );
}
